import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { ImageCropperModal } from "@/components/ImageCropperModal";

export default function AdminSiteSettings() {
  const [form, setForm] = useState({
    phone: "",
    email: "",
    address: "",
    social_links: [],
    about_us_text: "",
    contact_text: "",
    faq: [],
    logo_url: "",
    favicon_url: "",
  });
  const [saving, setSaving] = useState(false);
  
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropFile, setCropFile] = useState(null);
  const [cropField, setCropField] = useState("");
  const [cropAspect, setCropAspect] = useState(undefined);

  useEffect(() => {
    api.get("/api/v1/site-settings").then((r) => {
      if (r.data) {
        // Fallback for legacy data if needed, migrate them to social_links
        let loadedLinks = r.data.social_links || [];
        if (loadedLinks.length === 0 && (r.data.facebook || r.data.instagram || r.data.twitter)) {
          if (r.data.facebook) loadedLinks.push({ platform: "facebook", url: r.data.facebook });
          if (r.data.instagram) loadedLinks.push({ platform: "instagram", url: r.data.instagram });
          if (r.data.twitter) loadedLinks.push({ platform: "twitter", url: r.data.twitter });
        }

        setForm({
          phone: r.data.phone || "",
          email: r.data.email || "",
          address: r.data.address || "",
          social_links: loadedLinks,
          about_us_text: r.data.about_us_text || "",
          contact_text: r.data.contact_text || "",
          faq: r.data.faq || [],
          logo_url: r.data.logo_url || "",
          favicon_url: r.data.favicon_url || "",
        });
      }
    }).catch(console.error);
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/api/v1/admin/site-settings", form);
      toast.success("Site ayarları güncellendi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const addSocial = () => {
    setForm((f) => ({ ...f, social_links: [...f.social_links, { platform: "instagram", url: "" }] }));
  };

  const updateSocial = (i, field, val) => {
    const list = [...form.social_links];
    list[i][field] = val;
    setForm((f) => ({ ...f, social_links: list }));
  };

  const removeSocial = (i) => {
    const list = [...form.social_links];
    list.splice(i, 1);
    setForm((f) => ({ ...f, social_links: list }));
  };

  const addFaq = () => {
    setForm((f) => ({ ...f, faq: [...f.faq, { q: "", a: "" }] }));
  };

  const updateFaq = (i, field, val) => {
    const list = [...form.faq];
    list[i][field] = val;
    setForm((f) => ({ ...f, faq: list }));
  };

  const removeFaq = (i) => {
    const list = [...form.faq];
    list.splice(i, 1);
    setForm((f) => ({ ...f, faq: list }));
  };

  const handleFileSelect = (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // For favicon we want square aspect ratio (1:1), for logo we want free aspect ratio (undefined)
    setCropAspect(field === "favicon_url" ? 1 : undefined);
    setCropField(field);
    setCropFile(file);
    setCropModalOpen(true);
    e.target.value = null; // reset input
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setSaving(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("type", cropField === "favicon_url" ? "favicon" : "logo");
    try {
      const { data } = await api.post("/api/v1/upload/media", fd);
      setForm((f) => ({ ...f, [cropField]: data.url }));
      toast.success("Görsel yüklendi");
    } catch (err) {
      console.error("Upload error:", err);
      toast.error(err?.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="overline text-accent">Yönetim</div>
        <h1 className="font-display text-3xl font-bold">Site Ayarları</h1>
        <p className="text-muted-foreground text-sm mt-1">Logo, iletişim ve sosyal medya bilgilerini yönetin.</p>
      </div>

      <Tabs defaultValue="brand" className="w-full">
        <TabsList className="w-full flex justify-start mb-4 overflow-x-auto h-auto p-1 bg-muted/50">
          <TabsTrigger value="brand">Marka & Görseller</TabsTrigger>
          <TabsTrigger value="contact">İletişim Bilgileri</TabsTrigger>
          <TabsTrigger value="social">Sosyal Medya</TabsTrigger>
          <TabsTrigger value="pages">Sayfa İçerikleri (Hakkımızda & İletişim)</TabsTrigger>
          <TabsTrigger value="faq">S.S.S.</TabsTrigger>
        </TabsList>

        <TabsContent value="brand">
          <div className="bg-card border border-border p-6 rounded-lg space-y-6 shadow-sm">
            <h3 className="font-semibold text-lg border-b border-border pb-2">Marka & Görseller</h3>
            
            <div className="space-y-4">
              <div>
                <Label>Logo URL</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="/logo.png veya https://..." className="flex-1" />
                  <Button type="button" variant="outline" className="relative cursor-pointer">
                    Yükle
                    <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" accept="image/*" onChange={(e) => handleFileSelect(e, "logo_url")} />
                  </Button>
                </div>
                {form.logo_url && (
                  <div className="mt-4 p-4 border border-border rounded-lg bg-background inline-block">
                    <img src={form.logo_url.startsWith('http') ? form.logo_url : `${process.env.REACT_APP_BACKEND_URL || ''}${form.logo_url}`} alt="Logo" className="h-24 w-auto object-contain" />
                  </div>
                )}
              </div>
              
              <div>
                <Label>Favicon URL</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={form.favicon_url} onChange={(e) => setForm({ ...form, favicon_url: e.target.value })} placeholder="/favicon.png veya https://..." className="flex-1" />
                  <Button type="button" variant="outline" className="relative cursor-pointer">
                    Yükle
                    <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" accept="image/*,.ico" onChange={(e) => handleFileSelect(e, "favicon_url")} />
                  </Button>
                </div>
                {form.favicon_url && (
                  <div className="mt-4 p-4 border border-border rounded-lg bg-background inline-block">
                    <img src={form.favicon_url.startsWith('http') ? form.favicon_url : `${process.env.REACT_APP_BACKEND_URL || ''}${form.favicon_url}`} alt="Favicon" className="h-16 w-16 object-contain" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="contact">
          <div className="bg-card border border-border p-6 rounded-lg space-y-4 shadow-sm">
            <h3 className="font-semibold text-lg border-b border-border pb-2">İletişim Bilgileri</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Telefon</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="0850 000 00 00" />
              </div>
              <div className="space-y-2">
                <Label>E-posta</Label>
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="destek@sanostudent.com" />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Adres</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Şirket Adresi" />
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="social">
          <div className="bg-card border border-border p-6 rounded-lg space-y-4 shadow-sm">
            <div className="flex items-center justify-between border-b border-border pb-2">
              <h3 className="font-semibold text-lg">Sosyal Medya</h3>
              <Button variant="outline" size="sm" onClick={addSocial}>
                <Plus className="h-4 w-4 mr-1" /> Hesap Ekle
              </Button>
            </div>
            <div className="space-y-3">
              {form.social_links.length === 0 && (
                <div className="text-sm text-muted-foreground p-4 text-center border border-dashed rounded-lg">
                  Henüz sosyal medya hesabı eklenmemiş.
                </div>
              )}
              {form.social_links.map((link, i) => (
                <div key={i} className="flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-muted/30 p-2 rounded-lg border border-border">
                  <Select value={link.platform} onValueChange={(v) => updateSocial(i, "platform", v)}>
                    <SelectTrigger className="w-full sm:w-[150px] bg-background">
                      <SelectValue placeholder="Platform" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="instagram">Instagram</SelectItem>
                      <SelectItem value="facebook">Facebook</SelectItem>
                      <SelectItem value="twitter">Twitter (X)</SelectItem>
                      <SelectItem value="linkedin">LinkedIn</SelectItem>
                      <SelectItem value="youtube">YouTube</SelectItem>
                      <SelectItem value="tiktok">TikTok</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input 
                    className="flex-1 bg-background" 
                    value={link.url} 
                    onChange={(e) => updateSocial(i, "url", e.target.value)} 
                    placeholder="https://..." 
                  />
                  <Button variant="ghost" size="icon" className="text-destructive shrink-0 self-end sm:self-auto hover:bg-destructive/10" onClick={() => removeSocial(i)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="pages">
          <div className="bg-card border border-border p-6 rounded-lg space-y-4 shadow-sm mb-4">
            <h3 className="font-semibold text-lg border-b border-border pb-2">Hakkımızda Sayfası İçeriği</h3>
            <div className="space-y-2">
              <Label>Metin İçeriği</Label>
              <div className="border border-input rounded-md overflow-hidden bg-background prose max-w-none">
                <CKEditor
                  editor={ClassicEditor}
                  data={form.about_us_text || ""}
                  onChange={(event, editor) => {
                    const data = editor.getData();
                    setForm({ ...form, about_us_text: data });
                  }}
                />
              </div>
            </div>
          </div>

          <div className="bg-card border border-border p-6 rounded-lg space-y-4 shadow-sm">
            <h3 className="font-semibold text-lg border-b border-border pb-2">İletişim Sayfası İçeriği</h3>
            <div className="space-y-2">
              <Label>İletişim Sayfası Ek Açıklaması</Label>
              <div className="border border-input rounded-md overflow-hidden bg-background prose max-w-none">
                <CKEditor
                  editor={ClassicEditor}
                  data={form.contact_text || ""}
                  onChange={(event, editor) => {
                    const data = editor.getData();
                    setForm({ ...form, contact_text: data });
                  }}
                />
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="faq">
          <div className="bg-card border border-border p-6 rounded-lg space-y-4 shadow-sm">
            <div className="flex items-center justify-between border-b border-border pb-2">
              <h3 className="font-semibold text-lg">Sıkça Sorulan Sorular (SSS)</h3>
              <Button variant="outline" size="sm" onClick={addFaq}>
                <Plus className="h-4 w-4 mr-1" /> Soru Ekle
              </Button>
            </div>
            <div className="space-y-4">
              {form.faq.length === 0 && (
                <div className="text-sm text-muted-foreground p-4 text-center border border-dashed rounded-lg">
                  Henüz soru eklenmemiş.
                </div>
              )}
              {form.faq.map((item, i) => (
                <div key={i} className="flex flex-col gap-3 bg-muted/30 p-4 rounded-lg border border-border relative">
                  <Button variant="ghost" size="icon" className="text-destructive absolute top-2 right-2 hover:bg-destructive/10" onClick={() => removeFaq(i)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  <div className="space-y-2 pr-10">
                    <Label>Soru</Label>
                    <Input 
                      className="bg-background" 
                      value={item.q} 
                      onChange={(e) => updateFaq(i, "q", e.target.value)} 
                      placeholder="Örn: Sistem ücretli mi?" 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Cevap</Label>
                    <textarea 
                      className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                      value={item.a} 
                      onChange={(e) => updateFaq(i, "a", e.target.value)} 
                      placeholder="Cevap..." 
                      rows={3}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Kaydediliyor..." : "Ayarları Kaydet"}
        </Button>
      </div>

      <ImageCropperModal
        open={cropModalOpen}
        onOpenChange={setCropModalOpen}
        imageFile={cropFile}
        onCropComplete={handleUpload}
        aspect={cropAspect}
      />
    </div>
  );
}
